import React, { Component } from 'react';
import { withStyles, withTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { alpha } from '@material-ui/core/styles/colorManipulator';

// ui/ux
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// ours
import Waiting from '../Waiting';
import { uniqueId, convertSnakeCase } from '../../common';

// resources
import { ReactComponent as Good } from '../../resources/good.svg';
import { ReactComponent as Bad } from '../../resources/bad.svg';

// GLOBALS
const PNA_KEY = 'other';
const UNSURE_KEY = 'unsure';

const styles = (/* theme */) => ({
  svg: {
    '& text#raw': {
      display: 'none'
    },
    '&:hover text#perc': {
      display: 'none'
    },
    '&:hover text#raw': {
      display: 'initial'
    }
  },
  svgBigValues: {
    fontSize: '20px',
    fontWeight: 'bold'
  }
});

class ThumbsChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      id: uniqueId()
    };
  }

  // componentDidUpdate(prevProps, prevState) {
  //   const { classes, data } = this.props;
  //   const { id } = this.state;
  // }

  render() {
    const { classes, theme, data, title, width } = this.props;
    const { id } = this.state;

    if (data && Object.values(data).reduce((a, b) => a + b) === 0) {
      return null;
    }

    if (!data) {
      return (
        <Box style={{ height: width / 1.77 }}>
          <Typography variant="h6" gutterBottom>
            {convertSnakeCase(title)}
          </Typography>
          <Waiting />
        </Box>
      );
    }

    const total = Object.values(data).reduce((a, b) => a + b);

    const pna = !isNaN(data[PNA_KEY]) ? Math.round((data[PNA_KEY] / total) * 1000) / 10 : 0;
    const u = !isNaN(data[UNSURE_KEY]) ? Math.round((data[UNSURE_KEY] / total) * 1000) / 10 : 0;
    const y = Math.round((data.yes / total) * 1000) / 10;
    const n = Math.round((data.no / total) * 1000) / 10;
    const ns = 18;

    const s = theme.spacing(1);
    const svgWidth = width / 4 - s * 2;
    const goodX = width / 4;
    const badY = s + svgWidth * 0.396;
    const badX = width - svgWidth - s;
    const height = badY + svgWidth + 10 + ns * 2 + s * 3;
    const goodColor = y >= n ? theme.palette.secondary.main : alpha(theme.palette.secondary.main, 0.3);
    const badColor = y < n ? theme.palette.getContrastText(theme.palette.background.paper) : alpha(theme.palette.getContrastText(theme.palette.background.paper), 0.3);
    const textX = svgWidth * 0.62 - 21;
    return (
      <Box className={classes.container}>
        <Typography variant="h6" gutterBottom>
          {convertSnakeCase(title)}
        </Typography>
        <svg id={id} width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={classes.svg}>
          <Good fill={goodColor} width={svgWidth} height={svgWidth} x={goodX} y={s} />
          <Bad fill={badColor} width={svgWidth} height={svgWidth} x={badX - goodX} y={badY} />
          <text
            className={classes.svgBigValues}
            id="perc"
            fill={goodColor}
            textAnchor={'middle'}
            alignmentBaseline={'middle'}
            x={s + svgWidth * 0.62}
            y={s + svgWidth * 0.7}
          >{`${y}%`}</text>
          <text className={classes.svgBigValues} id="raw" fill={goodColor} textAnchor={'middle'} alignmentBaseline={'middle'} x={s + svgWidth * 0.62} y={s + svgWidth * 0.7}>
            {data.yes}
          </text>
          <text
            className={classes.svgBigValues}
            id="perc"
            fill={badColor}
            textAnchor={'middle'}
            alignmentBaseline={'middle'}
            x={badX + svgWidth * 0.38}
            y={s + svgWidth * 0.7}
          >{`${n}%`}</text>
          <text className={classes.svgBigValues} id="raw" fill={badColor} textAnchor={'middle'} alignmentBaseline={'middle'} x={badX + svgWidth * 0.38} y={s + svgWidth * 0.7}>
            {data.no}
          </text>

          <text
            id="perc"
            fontSize="14px"
            x={textX}
            y={height - 28 - s}
            textAnchor={'start'}
            alignmentBaseline={'hanging'}
            fill={theme.palette.getContrastText(theme.palette.background.paper)}
          >
            {pna > 0 ? `► ${pna}% respondents provided a different response` : ''}
          </text>
          <text
            id="raw"
            fontSize="14px"
            x={textX}
            y={height - 28 - s}
            textAnchor={'start'}
            alignmentBaseline={'hanging'}
            fill={theme.palette.getContrastText(theme.palette.background.paper)}
          >
            {pna > 0 ? `► ${data[PNA_KEY]} respondents provided a different response` : ''}
          </text>
          <text
            id="perc"
            fontSize="14px"
            x={textX}
            y={height - ns}
            textAnchor={'start'}
            alignmentBaseline={'hanging'}
            fill={theme.palette.getContrastText(theme.palette.background.paper)}
          >
            {u > 0 ? `► ${u}% respondents were unsure` : ''}
          </text>
          <text
            id="raw"
            fontSize="14px"
            x={textX}
            y={height - ns}
            textAnchor={'start'}
            alignmentBaseline={'hanging'}
            fill={theme.palette.getContrastText(theme.palette.background.paper)}
          >
            {u > 0 ? `► ${data[UNSURE_KEY]} respondents were unsure` : ''}
          </text>
        </svg>
      </Box>
    );
  }
}

ThumbsChart.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,

  data: PropTypes.object,
  title: PropTypes.string,

  width: PropTypes.number
};

ThumbsChart.defaultProps = {
  width: 400
};

export default withTheme(withStyles(styles)(ThumbsChart));
